import {
  useLayoutEffect,
  useState,
  useEffect,
  useCallback,
  useRef,
} from "react";
import DataGrid, {
  Column,
  Export,
  HeaderFilter,
  Pager,
  Paging,
  SearchPanel,
} from "devextreme-react/data-grid";
import { useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faRefresh } from "@fortawesome/free-solid-svg-icons";
import moment from "moment";
import { Template } from "devextreme-react";
import {
  IconWithTooltip,
  AssetQuotationType,
  AssetSubStatus,
} from "../../../helper/fixcode";
import { Loader } from "../../Shared/PageLoader";
import { assetService } from "../../../apis/apiServices/Asset";
import { reportService } from "../../../apis/apiServices/Report";

export default function ReportAssetHireComponent(props) {
  const navigate = useNavigate();
  const user = useSelector((state) => state.user.data);
  const [assetHireList, setAssetHireList] = useState([]);
  const refDataGrid = useRef(null);
  const [state, setState] = useState({
    isCompleteLoading: false,
    loadingMessage: "Loading Report...",
  });

  useLayoutEffect(() => {
    document.querySelector("#pageTitle").innerHTML = `Asset Hire`;
  }, []);

  let isUseEffectRendered = false;
  useEffect(() => {
    const controller = new AbortController();
    try {
      if (isUseEffectRendered) {
        return;
      }
      fetchApiOnPageLoad();
      return () => {
        controller.abort();
        isUseEffectRendered = true;
      };
    } catch (error) {
      console.log(error);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const fetchApiOnPageLoad = useCallback(async () => {
    try {
      setState((prevState) => ({
        ...prevState,
        isCompleteLoading: true,
      }));
      await getAssetHireList();
    } catch (error) {
      console.log(error);
    } finally {
      setState((prevState) => ({
        ...prevState,
        isCompleteLoading: false,
      }));
    }
  }, []);

  const getAssetHireList = async () => {
    try {
      const body = {
        requestQuotationId: 0,
        statusId: 0,
        userId: user?.userId ?? 0,
        quotationTypeId: AssetQuotationType.RequestForAssetServiceOrder,
      };
      const response = await reportService.getAssetQuotationList(body);
      setAssetHireList(response?.data ?? []);
    } catch (error) {
      console.log(error);
    }
  };

  return (
    <>
      <div className="content">
        <div className="card">
          <div className="card-body">
            <div className="row">
              <div className="col-12">
                <DataGrid
                  ref={refDataGrid}
                  dataSource={assetHireList}
                  keyExpr="requestQuotationId"
                  showColumnLines={false}
                  showRowLines={true}
                  showBorders={true}
                  allowColumnResizing={false}
                  allowColumnReordering={true}
                  columnAutoWidth={true}
                  onToolbarPreparing={(e) => {
                    e.toolbarOptions.items.unshift({
                      location: "before",
                      template: "toolbarRefreshTemplate",
                    });
                  }}
                >
                  {/* action column */}
                  <Column
                    dataField="requestQuotationId"
                    width={40}
                    caption={""}
                    allowExporting={false}
                    allowSearch={false}
                    allowFiltering={false}
                    allowSorting={false}
                    cellRender={(data) => {
                      const row = data?.data;
                      const idPrefix = `icon-${row?.requestQuotationId}`;
                      return (
                        <div style={{ display: "flex" }}>
                          {props?.featurePermission?.canView && (
                            <span>
                              <IconWithTooltip
                                id={`${idPrefix}-view`}
                                className="iconify-size mr-1"
                                title="View"
                                tooltip="View"
                                icon="solar:document-text-outline"
                                style={{ cursor: "pointer" }}
                                onClick={() =>
                                  navigate("/asset-hire-preview", {
                                    state: {
                                      requestQuotationId:
                                        row?.requestQuotationId,
                                      quotationIndentNo: row?.quotationIndentNo,
                                      quotationTypeId: row?.quotationTypeId,
                                      sourceUrl: "ReportAssetHire",
                                      isReport: true,
                                    },
                                  })
                                }
                              />
                            </span>
                          )}
                        </div>
                      );
                    }}
                  />

                  <Column
                    dataField="quotationIndentNo"
                    caption="Quotation No"
                    allowSearch={true}
                    allowFiltering={false}
                    allowSorting={true}
                  />

                  <Column
                    dataField="assetStatusName"
                    caption="Status"
                    allowSearch={true}
                    allowFiltering={false}
                    allowSorting={true}
                    cellRender={(e) => {
                      let color = "#28A745";
                      if (
                        e.data?.statusId ===
                        AssetSubStatus.AssetQuotationInDraftMode
                      )
                        color = "#494545ff";
                      else if (
                        e.data?.statusId ===
                        AssetSubStatus.AssetQuotationApproved
                      )
                        color = "#4CAF50";
                      else if (
                        e.data?.statusId ===
                        AssetSubStatus.AssetQuotationSendforApproval
                      )
                        color = "#FF9800";

                      return (
                        <div>
                          <span style={{ color }}>
                            {e.data?.assetStatusName}
                          </span>
                        </div>
                      );
                    }}
                  />

                  <Column
                    dataField="requestedDate"
                    caption="Quotation Date"
                    allowSearch={true}
                    allowFiltering={false}
                    allowSorting={true}
                    dataType="date"
                    format="dd-MMM-yyyy"
                    cellRender={(cell) => {
                      // sample requestedDate: "12/11/2025 04:40:11" or ISO string; try to parse with moment
                      const raw = cell?.data?.requestedDate;
                      if (!raw) return "";
                      const m = moment(raw, moment.ISO_8601, true);
                      const parsed = m.isValid()
                        ? m
                        : moment(raw, "DD/MM/YYYY HH:mm:ss", true);
                      return parsed.isValid()
                        ? parsed.format("DD-MMM-YYYY HH:mm")
                        : raw;
                    }}
                  />

                  <Column
                    dataField="assetRequestSubNo"
                    caption="Asset Request No"
                    allowSearch={true}
                    allowFiltering={false}
                    allowSorting={true}
                  />

                  <Column
                    dataField="toProjectName"
                    caption="Project"
                    allowSearch={true}
                    allowSorting={true}
                    allowFiltering={false}
                    cellRender={(data) => {
                      if (data?.data?.toProjectName) {
                        return (
                          <>
                            <span>{data?.data?.toProjectName ?? ""}</span>
                            <br />
                            <div style={{ color: "#b1b1b1", fontSize: "11px" }}>
                              {data?.data?.toProjectName ?? ""}
                            </div>
                          </>
                        );
                      } else {
                        return "";
                      }
                    }}
                  />

                  <Column
                    dataField="assetDescriptions"
                    caption="Asset Description"
                    allowSearch={true}
                    allowFiltering={false}
                    allowSorting={true}
                  />

                  <Column
                    dataField="fromName"
                    caption="From"
                    allowSearch={true}
                    allowSorting={true}
                    allowFiltering={false}
                    cellRender={(data) => {
                      if (data?.data?.fromName) {
                        return (
                          <>
                            <span>{data?.data?.fromName ?? ""}</span>
                            <br />
                            <div style={{ color: "#b1b1b1", fontSize: "11px" }}>
                              {data?.data?.fromDepartment ?? ""}
                            </div>
                          </>
                        );
                      } else {
                        return "";
                      }
                    }}
                  />

                  <Column
                    dataField="toName"
                    caption="To"
                    allowSearch={true}
                    allowSorting={true}
                    allowFiltering={false}
                    cellRender={(data) => {
                      if (data?.data?.toName) {
                        return (
                          <>
                            <span>{data?.data?.toName ?? ""}</span>
                            <br />
                            <div style={{ color: "#b1b1b1", fontSize: "11px" }}>
                              {data?.data?.toDepartment ?? ""}
                            </div>
                          </>
                        );
                      } else {
                        return "";
                      }
                    }}
                  />

                  <Template
                    name="toolbarRefreshTemplate"
                    render={() => {
                      return (
                        <>
                          <button
                            className="btn btn-outline-primary btn-sm"
                            onClick={async () => {
                              await getAssetHireList();
                            }}
                            title="Refresh"
                          >
                            <FontAwesomeIcon icon={faRefresh} />
                            &nbsp;Refresh
                          </button>
                        </>
                      );
                    }}
                  />

                  <HeaderFilter visible={false} />
                  <SearchPanel
                    visible={true}
                    highlightCaseSensitive={true}
                    width={240}
                    placeholder="Search..."
                  />
                  <Paging defaultPageSize={15} />
                  <Pager displayMode="compact" showInfo={true} visible={true} />
                  <Export enabled={true} fileName="Asset Hire Report" />
                </DataGrid>
              </div>
            </div>
          </div>
        </div>
      </div>

      {state.isCompleteLoading && (
        <Loader loadingMessage={state.loadingMessage} />
      )}
    </>
  );
}
